import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Check, Package, HelpCircle, Search, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DocumentWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState({
    title: '',
    tipo: location.state?.tipo || 'Proposta Comercial',
    client_id: '',
    numero_documento: '',
    template_id: '',
    variables: {},
    contextualizacao: '',
    optional_sections: {},
    items: []
  });

  // Diagnostic state
  const [opportunityData, setOpportunityData] = useState({
    name: '', numero_processo: '', descricao: ''
  });
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [diagnosticQuestions, setDiagnosticQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [productSearch, setProductSearch] = useState('');
  const [premissas, setPremissas] = useState({});
  const [catalogVariables, setCatalogVariables] = useState([]);

  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [docVarValues, setDocVarValues] = useState({});
  const [docVarsForType, setDocVarsForType] = useState([]);

  const requiredDocVars = useMemo(
    () => docVarsForType.filter(v => v.required),
    [docVarsForType]
  );
  const optionalDocVars = useMemo(
    () => docVarsForType.filter(v => !v.required),
    [docVarsForType]
  );

  const loadClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`, { withCredentials: true });
      setClients(response.data);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/templates?tipo=${formData.tipo}`, { withCredentials: true });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Erro ao carregar modelos');
    }
  }, [formData.tipo]);

  const loadProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/products`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    }
  }, []);

  const loadCatalogVariables = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/variables`, { withCredentials: true });
      setCatalogVariables(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    loadClients();
    loadProducts();
    loadCatalogVariables();
  }, [loadClients, loadProducts, loadCatalogVariables]);

  useEffect(() => {
    if (formData.tipo) loadTemplates();
  }, [formData.tipo, loadTemplates]);

  // Load doc variables for the selected document type
  useEffect(() => {
    const loadDocVars = async () => {
      if (!formData.tipo) return;
      try {
        const clientParam = formData.client_id ? `&client_id=${formData.client_id}` : '';
        const res = await axios.get(`${API_URL}/doc-variables/for-document/${encodeURIComponent(formData.tipo)}?${clientParam}`, { withCredentials: true });
        setDocVarsForType(res.data);
        // Pre-fill auto values
        const autoFilled = {};
        res.data.forEach(v => {
          if (v.auto_value) autoFilled[v.name] = v.auto_value;
          else if (v.default_value && !docVarValues[v.name]) autoFilled[v.name] = v.default_value;
        });
        if (Object.keys(autoFilled).length > 0) {
          setDocVarValues(prev => ({ ...autoFilled, ...prev }));
        }
      } catch {}
    };
    loadDocVars();
  }, [formData.tipo, formData.client_id]);

  // Load diagnostic questions when products are selected
  useEffect(() => {
    const loadQuestions = async () => {
      if (selectedProductIds.length === 0) {
        // Load only generic questions
        try {
          const res = await axios.get(`${API_URL}/diagnostic-questions?category=generica`, { withCredentials: true });
          setDiagnosticQuestions(res.data);
        } catch (error) {
          console.error('Failed to load questions:', error);
        }
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/diagnostic-questions/for-products?product_ids=${selectedProductIds.join(',')}`, { withCredentials: true });
        setDiagnosticQuestions(res.data);
      } catch (error) {
        console.error('Failed to load questions:', error);
      }
    };
    loadQuestions();
  }, [selectedProductIds]);

  const toggleProduct = (productId) => {
    setSelectedProductIds(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
    );
  };

  // Collect linked variables from selected products
  const linkedVarsFromProducts = (() => {
    const varMap = {};
    selectedProductIds.forEach(pid => {
      const product = products.find(p => p.product_id === pid);
      if (product?.linked_variables) {
        product.linked_variables.forEach(lv => {
          if (!varMap[lv.var_name]) {
            varMap[lv.var_name] = { var_name: lv.var_name, required: lv.required, products: [product.name] };
          } else {
            if (lv.required) varMap[lv.var_name].required = true;
            varMap[lv.var_name].products.push(product.name);
          }
        });
      }
    });
    return Object.values(varMap);
  })();

  const requiredVars = linkedVarsFromProducts.filter(v => v.required);
  const optionalVars = linkedVarsFromProducts.filter(v => !v.required);

  // Group questions by group
  const groupedQuestions = {};
  diagnosticQuestions.forEach(q => {
    const g = q.group || 'Geral';
    if (!groupedQuestions[g]) groupedQuestions[g] = [];
    groupedQuestions[g].push(q);
  });

  const handleNext = () => {
    if (currentStep === 1) {
      if (!formData.title || !formData.client_id) {
        toast.error('Preencha título e selecione o cliente');
        return;
      }
      if (selectedProductIds.length === 0) {
        toast.error('Selecione ao menos um produto/serviço');
        return;
      }
      // Sync items from selected products
      const newItems = selectedProductIds.map(pid => {
        const existing = formData.items.find(i => i.product_id === pid);
        return existing || { product_id: pid, quantity: 1, unit_price_override: null };
      });
      setFormData(prev => ({ ...prev, items: newItems }));
    }
    if (currentStep === 2) {
      // Validate required premissas
      const missingRequired = requiredVars.filter(v => !premissas[v.var_name] || !premissas[v.var_name].trim());
      if (missingRequired.length > 0) {
        toast.error(`Preencha as premissas obrigatorias: ${missingRequired.map(v => v.var_name).join(', ')}`);
        return;
      }
    }
    if (currentStep === 3) {
      if (!formData.template_id) {
        toast.error('Selecione um modelo');
        return;
      }
      // Validate required doc variables
      const missingDocVars = docVarsForType
        .filter(v => v.required && !docVarValues[v.name]?.trim())
        .map(v => v.label || v.name);
      if (missingDocVars.length > 0) {
        toast.error(`Preencha os campos obrigatorios: ${missingDocVars.slice(0, 3).join(', ')}${missingDocVars.length > 3 ? '...' : ''}`);
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => setCurrentStep(currentStep - 1);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // 1. Create opportunity
      const answersList = Object.entries(answers)
        .filter(([, v]) => v)
        .map(([question_id, value]) => ({ question_id, value }));

      const oppRes = await axios.post(`${API_URL}/opportunities`, {
        name: opportunityData.name || formData.title,
        client_id: formData.client_id,
        tipo_documento: formData.tipo,
        numero_processo: opportunityData.numero_processo,
        descricao: opportunityData.descricao,
        product_ids: selectedProductIds,
        answers: answersList
      }, { withCredentials: true });

      // 2. Map diagnostic answers to document variables
      const diagVars = {};
      diagnosticQuestions.forEach(q => {
        if (q.variable_name && answers[q.question_id]) {
          diagVars[q.variable_name] = answers[q.question_id];
        }
      });

      // 3. Create document with merged variables (diag + doc vars)
      const mergedVariables = { ...diagVars, ...docVarValues, ...formData.variables };
      const docRes = await axios.post(`${API_URL}/documents`, {
        ...formData,
        variables: mergedVariables
      }, { withCredentials: true });

      // 3.5 Save premissas to document
      const filledPremissas = {};
      Object.entries(premissas).forEach(([k, v]) => { if (v) filledPremissas[k] = v; });
      if (Object.keys(filledPremissas).length > 0) {
        await axios.put(`${API_URL}/documents/${docRes.data.document_id}/premissas`, {
          premissas: filledPremissas
        }, { withCredentials: true });
      }

      // 4. Link document to opportunity
      await axios.put(`${API_URL}/opportunities/${oppRes.data.opportunity_id}`, {
        answers: answersList
      }, { withCredentials: true });

      toast.success('Documento criado com sucesso!');
      navigate(`/documents/${docRes.data.document_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar documento');
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const loadPricesFromTable = async () => {
    setLoadingPrices(true);
    let updated = 0;
    try {
      const pricesRes = await axios.get(`${API_URL}/prices`, { withCredentials: true });
      const allPrices = pricesRes.data;
      const newItems = formData.items.map(item => {
        const matching = allPrices.filter(p => p.product_id === item.product_id);
        if (matching.length === 0) return item;
        // Find tier that fits the quantity
        const tier = matching.find(p => item.quantity >= (p.min_qty || 0) && item.quantity <= (p.max_qty || 999999999));
        const price = tier || matching[0]; // fallback to first price
        if (price) {
          updated++;
          return { ...item, unit_price_override: price.unit_price };
        }
        return item;
      });
      setFormData(prev => ({ ...prev, items: newItems }));
      toast.success(`${updated} item(s) atualizado(s) com preços da tabela`);
    } catch (error) {
      toast.error('Erro ao carregar preços');
    } finally {
      setLoadingPrices(false);
    }
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => {
      const price = item.unit_price_override || 0;
      return sum + (price * item.quantity);
    }, 0);
  };

  const filteredProducts = products.filter(p =>
    !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()) || p.codigo?.toLowerCase().includes(productSearch.toLowerCase())
  );

  const steps = [
    { number: 1, title: 'Definição Inicial' },
    { number: 2, title: 'Diagnóstico' },
    { number: 3, title: 'Modelo' },
    { number: 4, title: 'Quantidades' },
    { number: 5, title: 'Preços e Revisão' }
  ];

  return (
    <div className="max-w-4xl mx-auto" data-testid="document-wizard">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                  step.number < currentStep ? 'bg-[#34C759] text-white'
                    : step.number === currentStep ? 'bg-[#AF2628] text-white'
                    : 'bg-[#E5E5EA] text-[#6E6E73]'
                }`}>
                  {step.number < currentStep ? <Check className="w-5 h-5" /> : step.number}
                </div>
                <span className="text-[11px] mt-1.5 text-[#6E6E73] text-center max-w-[80px]">{step.title}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${step.number < currentStep ? 'bg-[#34C759]' : 'bg-[#E5E5EA]'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#E5E5EA] rounded-xl p-8">
        {/* STEP 1: Definição Inicial + Seleção de Produtos */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-[#111111]">Definição Inicial</h2>
            <p className="text-sm text-[#6E6E73]">Identifique o cliente, tipo de documento e selecione os produtos/serviços</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Título do Documento <span className="text-[#AF2628]">*</span></Label>
                <Input value={formData.title} onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))} placeholder="Ex: Proposta para Modernização da Gestão Patrimonial" className="mt-1" data-testid="wizard-title" />
              </div>
              <div>
                <Label>Identificação / Referência</Label>
                <Input value={opportunityData.name} onChange={e => setOpportunityData(prev => ({ ...prev, name: e.target.value }))} placeholder="Ex: Pregão 001/2026 - Câmara Municipal" className="mt-1" data-testid="wizard-opp-name" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Tipo de Documento <span className="text-[#AF2628]">*</span></Label>
                <Select value={formData.tipo} onValueChange={val => setFormData(prev => ({ ...prev, tipo: val }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Proposta Comercial">Proposta Comercial</SelectItem>
                    <SelectItem value="ETP">Estudo Técnico Preliminar (ETP)</SelectItem>
                    <SelectItem value="TR">Termo de Referência (TR)</SelectItem>
                    <SelectItem value="Documento Auxiliar">Documento Auxiliar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cliente <span className="text-[#AF2628]">*</span></Label>
                <Select value={formData.client_id} onValueChange={val => setFormData(prev => ({ ...prev, client_id: val }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.razao_social}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nº Processo / Licitação</Label>
                <Input value={opportunityData.numero_processo} onChange={e => setOpportunityData(prev => ({ ...prev, numero_processo: e.target.value }))} placeholder="PE 001/2026" className="mt-1" data-testid="wizard-processo" />
              </div>
            </div>

            {/* Product Selection */}
            <div>
              <Label>Produtos / Serviços <span className="text-[#AF2628]">*</span></Label>
              <p className="text-xs text-[#6E6E73] mb-2">Selecione os itens desta oportunidade. O questionário será montado com base neles.</p>
              <div className="relative mb-2">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6E6E73]" />
                <Input placeholder="Buscar produto..." value={productSearch} onChange={e => setProductSearch(e.target.value)} className="pl-10" data-testid="wizard-product-search" />
              </div>
              <div className="border border-[#E5E5EA] rounded-lg max-h-56 overflow-y-auto">
                {filteredProducts.map(p => (
                  <label
                    key={p.product_id}
                    className={`flex items-center gap-3 p-3 border-b border-[#E5E5EA] last:border-b-0 cursor-pointer hover:bg-[#FAFAFA] transition-colors ${selectedProductIds.includes(p.product_id) ? 'bg-[#FFF1F0]' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedProductIds.includes(p.product_id)}
                      onChange={() => toggleProduct(p.product_id)}
                      className="rounded border-[#E5E5EA]"
                    />
                    <Package className="w-4 h-4 text-[#6E6E73] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-[#111111]">{p.name}</span>
                      {p.descricao_resumida && <p className="text-xs text-[#AEAEB2] truncate">{p.descricao_resumida}</p>}
                    </div>
                    {p.codigo && <span className="text-[10px] text-[#AEAEB2] shrink-0">{p.codigo}</span>}
                  </label>
                ))}
              </div>
              {selectedProductIds.length > 0 && (
                <p className="text-xs text-[#AF2628] mt-2 font-medium">{selectedProductIds.length} produto(s) selecionado(s)</p>
              )}
            </div>
          </div>
        )}

        {/* STEP 2: Diagnóstico (Questionário) */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-[#111111]">Diagnóstico da Oportunidade</h2>
            <p className="text-sm text-[#6E6E73]">Preencha o questionário abaixo. As respostas serão usadas como variáveis nos documentos.</p>

            {Object.keys(groupedQuestions).length > 0 ? (
              Object.entries(groupedQuestions).map(([groupName, questions]) => (
                <div key={groupName} className="border border-[#E5E5EA] rounded-xl overflow-hidden">
                  <div className="bg-[#F7F7F8] px-4 py-3 border-b border-[#E5E5EA]">
                    <h3 className="text-sm font-semibold text-[#111111]">{groupName}</h3>
                  </div>
                  <div className="p-4 space-y-4">
                    {questions.map(q => (
                      <div key={q.question_id} data-testid={`diag-question-${q.question_id}`}>
                        <Label className="text-sm flex items-center gap-2">
                          <HelpCircle className="w-3.5 h-3.5 text-[#AF2628]" />
                          {q.text}
                          {q.required && <span className="text-[10px] text-red-500">*</span>}
                          {q.variable_name && (
                            <span className="text-[10px] font-mono bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded">
                              {'{{' + q.variable_name + '}}'}
                            </span>
                          )}
                        </Label>
                        {q.help_text && <p className="text-[11px] text-[#AEAEB2] mt-0.5 ml-5">{q.help_text}</p>}

                        <div className="mt-1.5 ml-5">
                          {q.type === 'texto' && (
                            <Input value={answers[q.question_id] || ''} onChange={e => setAnswers(prev => ({ ...prev, [q.question_id]: e.target.value }))} placeholder="Digite a resposta..." />
                          )}
                          {q.type === 'numero' && (
                            <Input type="number" value={answers[q.question_id] || ''} onChange={e => setAnswers(prev => ({ ...prev, [q.question_id]: e.target.value }))} placeholder="0" />
                          )}
                          {q.type === 'data' && (
                            <Input type="date" value={answers[q.question_id] || ''} onChange={e => setAnswers(prev => ({ ...prev, [q.question_id]: e.target.value }))} />
                          )}
                          {q.type === 'sim_nao' && (
                            <Select value={answers[q.question_id] || ''} onValueChange={v => setAnswers(prev => ({ ...prev, [q.question_id]: v }))}>
                              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Sim">Sim</SelectItem>
                                <SelectItem value="Não">Não</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                          {q.type === 'selecao' && (
                            <Select value={answers[q.question_id] || ''} onValueChange={v => setAnswers(prev => ({ ...prev, [q.question_id]: v }))}>
                              <SelectTrigger><SelectValue placeholder="Selecione uma opção" /></SelectTrigger>
                              <SelectContent>
                                {(q.options || []).map((opt, i) => <SelectItem key={`${opt}-${i}`} value={opt}>{opt}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        {q.category === 'produto' && q.product_names?.length > 0 && (
                          <div className="flex items-center gap-1 ml-5 mt-1">
                            <Package className="w-3 h-3 text-[#AEAEB2]" />
                            <span className="text-[10px] text-[#AEAEB2]">{q.product_names.join(', ')}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 bg-[#F7F7F8] rounded-xl">
                <HelpCircle className="w-8 h-8 mx-auto mb-2 text-[#E5E5EA]" />
                <p className="text-sm text-[#6E6E73]">Nenhuma pergunta cadastrada no Banco de Perguntas</p>
              </div>
            )}

            {/* Premissas Section */}
            {linkedVarsFromProducts.length > 0 && (
              <div className="border-2 border-[#AF2628]/20 rounded-xl overflow-hidden" data-testid="wizard-premissas-section">
                <div className="bg-[#FFF1F0] px-4 py-3 border-b border-[#AF2628]/20">
                  <h3 className="text-sm font-semibold text-[#AF2628]">Premissas do Documento</h3>
                  <p className="text-[11px] text-[#AF2628]/70 mt-0.5">
                    Variaveis exigidas pelos produtos selecionados. Campos com * sao obrigatorios.
                  </p>
                </div>
                <div className="p-4 space-y-4">
                  {requiredVars.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-red-600 uppercase tracking-wide">Obrigatorias</p>
                      {requiredVars.map(v => {
                        const catVar = catalogVariables.find(cv => cv.name === v.var_name);
                        return (
                          <div key={v.var_name} data-testid={`premissa-required-${v.var_name}`}>
                            <Label className="text-sm flex items-center gap-2">
                              <span className="font-mono text-[#AF2628] font-bold text-xs">{v.var_name}</span>
                              <span className="text-red-500 text-xs">*</span>
                            </Label>
                            <p className="text-[11px] text-[#6E6E73]">{catVar?.label || v.var_name}</p>
                            <Input
                              value={premissas[v.var_name] || ''}
                              onChange={e => setPremissas(prev => ({ ...prev, [v.var_name]: e.target.value }))}
                              placeholder={catVar?.default_value ? `Padrao: ${catVar.default_value}` : 'Informe o valor'}
                              className="mt-1"
                              data-testid={`premissa-input-${v.var_name}`}
                            />
                            <div className="flex items-center gap-1 mt-1">
                              <Package className="w-3 h-3 text-[#AEAEB2]" />
                              <span className="text-[10px] text-[#AEAEB2]">{v.products.join(', ')}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {optionalVars.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-[#6E6E73] uppercase tracking-wide">Opcionais</p>
                      {optionalVars.map(v => {
                        const catVar = catalogVariables.find(cv => cv.name === v.var_name);
                        return (
                          <div key={v.var_name} data-testid={`premissa-optional-${v.var_name}`}>
                            <Label className="text-sm flex items-center gap-2">
                              <span className="font-mono text-[#6E6E73] font-bold text-xs">{v.var_name}</span>
                            </Label>
                            <p className="text-[11px] text-[#AEAEB2]">{catVar?.label || v.var_name}</p>
                            <Input
                              value={premissas[v.var_name] || ''}
                              onChange={e => setPremissas(prev => ({ ...prev, [v.var_name]: e.target.value }))}
                              placeholder={catVar?.default_value ? `Padrao: ${catVar.default_value}` : 'Opcional'}
                              className="mt-1"
                              data-testid={`premissa-input-${v.var_name}`}
                            />
                            <div className="flex items-center gap-1 mt-1">
                              <Package className="w-3 h-3 text-[#AEAEB2]" />
                              <span className="text-[10px] text-[#AEAEB2]">{v.products.join(', ')}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 3: Modelo e Contexto */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-[#111111]">Modelo e Configurações</h2>

            <div>
              <Label>Selecione o Modelo <span className="text-[#AF2628]">*</span></Label>
              <Select
                value={formData.template_id}
                onValueChange={val => { setFormData(prev => ({ ...prev, template_id: val })); setSelectedTemplate(templates.find(t => t.template_id === val)); }}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder="Escolha um modelo" /></SelectTrigger>
                <SelectContent>
                  {templates.map(t => <SelectItem key={t.template_id} value={t.template_id}>{t.name} {t.cenario && `- ${t.cenario}`}</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedTemplate?.cenario_descricao && (
                <div className="mt-3 p-3 bg-[#F0F9FF] border border-[#0EA5E9] rounded-lg">
                  <p className="text-sm"><strong>Cenário:</strong> {selectedTemplate.cenario_descricao}</p>
                </div>
              )}
            </div>

            {(formData.tipo === 'ETP' || formData.tipo === 'TR') && (
              <div>
                <Label>Contextualização do Cliente</Label>
                <Textarea value={formData.contextualizacao} onChange={e => setFormData(prev => ({ ...prev, contextualizacao: e.target.value }))} placeholder="Descreva o contexto específico deste cliente..." rows={5} className="mt-1" />
              </div>
            )}

            <div>
              <Label>Número do Documento</Label>
              <Input value={formData.numero_documento} onChange={e => setFormData(prev => ({ ...prev, numero_documento: e.target.value }))} placeholder="Gerado automaticamente se vazio" className="mt-1" />
            </div>

            {/* Document Variables */}
            {docVarsForType.length > 0 && (
              <div className="border-2 border-[#AF2628]/20 rounded-xl overflow-hidden" data-testid="wizard-doc-variables">
                <div className="bg-[#FFF1F0] px-4 py-3 border-b border-[#AF2628]/20">
                  <h3 className="text-sm font-semibold text-[#AF2628]">Variaveis do Documento</h3>
                  <p className="text-[11px] text-[#AF2628]/70 mt-0.5">Preencha os campos abaixo. Campos com * sao obrigatorios. Campos automaticos ja estao preenchidos.</p>
                </div>
                <div className="p-4 space-y-4">
                  {requiredDocVars.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-red-600 uppercase tracking-wide">Obrigatorias</p>
                      {requiredDocVars.map(v => (
                        <div key={v.var_id} data-testid={`doc-var-required-${v.name}`}>
                          <Label className="text-sm flex items-center gap-2">
                            {v.label} <span className="text-red-500">*</span>
                            <span className="text-[10px] font-mono bg-[#F7F7F8] px-1.5 py-0.5 rounded text-[#6E6E73]">{`{{${v.name}}}`}</span>
                            {v.auto_fill_source && <span className="text-[10px] bg-green-50 text-green-600 px-1 rounded">Auto</span>}
                          </Label>
                          {v.description && <p className="text-[11px] text-[#AEAEB2]">{v.description}</p>}
                          {v.field_type === 'textarea' ? (
                            <Textarea value={docVarValues[v.name] || ''} onChange={e => setDocVarValues(prev => ({ ...prev, [v.name]: e.target.value }))} rows={3} className="mt-1" data-testid={`doc-var-input-${v.name}`} />
                          ) : v.field_type === 'data' ? (
                            <Input type="date" value={docVarValues[v.name] || ''} onChange={e => setDocVarValues(prev => ({ ...prev, [v.name]: e.target.value }))} className="mt-1" data-testid={`doc-var-input-${v.name}`} />
                          ) : v.field_type === 'numero' ? (
                            <Input type="number" step="any" value={docVarValues[v.name] || ''} onChange={e => setDocVarValues(prev => ({ ...prev, [v.name]: e.target.value }))} className="mt-1" data-testid={`doc-var-input-${v.name}`} />
                          ) : v.field_type === 'sim_nao' ? (
                            <Select value={docVarValues[v.name] || ''} onValueChange={val => setDocVarValues(prev => ({ ...prev, [v.name]: val }))}>
                              <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Sim">Sim</SelectItem>
                                <SelectItem value="Não">Não</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input value={docVarValues[v.name] || ''} onChange={e => setDocVarValues(prev => ({ ...prev, [v.name]: e.target.value }))} className="mt-1" data-testid={`doc-var-input-${v.name}`} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {optionalDocVars.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-[#6E6E73] uppercase tracking-wide">Opcionais</p>
                      {optionalDocVars.map(v => (
                        <div key={v.var_id} data-testid={`doc-var-optional-${v.name}`}>
                          <Label className="text-sm flex items-center gap-2">
                            {v.label}
                            <span className="text-[10px] font-mono bg-[#F7F7F8] px-1.5 py-0.5 rounded text-[#6E6E73]">{`{{${v.name}}}`}</span>
                            {v.auto_fill_source && <span className="text-[10px] bg-green-50 text-green-600 px-1 rounded">Auto</span>}
                          </Label>
                          {v.field_type === 'textarea' ? (
                            <Textarea value={docVarValues[v.name] || ''} onChange={e => setDocVarValues(prev => ({ ...prev, [v.name]: e.target.value }))} rows={2} className="mt-1" data-testid={`doc-var-input-${v.name}`} />
                          ) : v.field_type === 'data' ? (
                            <Input type="date" value={docVarValues[v.name] || ''} onChange={e => setDocVarValues(prev => ({ ...prev, [v.name]: e.target.value }))} className="mt-1" data-testid={`doc-var-input-${v.name}`} />
                          ) : v.field_type === 'numero' ? (
                            <Input type="number" step="any" value={docVarValues[v.name] || ''} onChange={e => setDocVarValues(prev => ({ ...prev, [v.name]: e.target.value }))} className="mt-1" data-testid={`doc-var-input-${v.name}`} />
                          ) : (
                            <Input value={docVarValues[v.name] || ''} onChange={e => setDocVarValues(prev => ({ ...prev, [v.name]: e.target.value }))} className="mt-1" data-testid={`doc-var-input-${v.name}`} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 4: Quantidades */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-[#111111]">Quantidades por Item</h2>
            <p className="text-sm text-[#6E6E73]">Defina a quantidade de cada produto/serviço selecionado</p>

            <div className="space-y-3">
              {formData.items.map((item, index) => {
                const product = products.find(p => p.product_id === item.product_id);
                if (!product) return null;
                return (
                  <div key={item.product_id} className="border border-[#E5E5EA] rounded-lg p-4 flex items-center gap-4" data-testid={`wizard-item-${index}`}>
                    <div className="bg-[#F7F7F8] w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold text-[#6E6E73]">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#111111]">{product.name}</p>
                      {product.unidade && <p className="text-xs text-[#AEAEB2]">Unidade: {product.unidade}</p>}
                    </div>
                    <div className="w-32">
                      <Label className="text-xs">Quantidade</Label>
                      <Input type="number" min="1" value={item.quantity} onChange={e => updateItem(index, 'quantity', parseInt(e.target.value) || 1)} data-testid={`item-qty-${index}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 5: Preços e Revisão Final */}
        {currentStep === 5 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#111111]">Revisão Final de Preços</h2>
              <Button
                variant="outline"
                onClick={loadPricesFromTable}
                disabled={loadingPrices}
                className="border-[#AF2628] text-[#AF2628] hover:bg-[#FFF1F0]"
                data-testid="load-prices-btn"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                {loadingPrices ? 'Carregando...' : 'Carregar Preços da Tabela'}
              </Button>
            </div>

            <div className="border border-[#E5E5EA] rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#F7F7F8]">
                  <tr>
                    <th className="text-left p-3 text-xs font-medium text-[#6E6E73]">ITEM</th>
                    <th className="text-center p-3 text-xs font-medium text-[#6E6E73]">QTD</th>
                    <th className="text-center p-3 text-xs font-medium text-[#6E6E73]">VALOR UNIT. (R$)</th>
                    <th className="text-right p-3 text-xs font-medium text-[#6E6E73]">TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => {
                    const product = products.find(p => p.product_id === item.product_id);
                    if (!product) return null;
                    const price = item.unit_price_override || 0;
                    const total = price * item.quantity;
                    return (
                      <tr key={`${item.product_id}-${index}`} className="border-t border-[#E5E5EA]">
                        <td className="p-3 text-sm">{product.name}</td>
                        <td className="p-3 text-sm text-center">{item.quantity}</td>
                        <td className="p-3">
                          <div className="flex justify-center">
                            <Input type="number" min="0" step="0.01" value={item.unit_price_override || ''} onChange={e => updateItem(index, 'unit_price_override', parseFloat(e.target.value) || 0)} placeholder="0,00" className="w-36 text-right" data-testid={`item-price-${index}`} />
                          </div>
                        </td>
                        <td className="p-3 text-sm text-right font-medium">R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-[#F7F7F8] font-bold">
                  <tr>
                    <td colSpan="3" className="p-3 text-right">VALOR TOTAL</td>
                    <td className="p-3 text-right text-[#AF2628]">R$ {calculateTotal().toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Diagnostic Summary */}
            {Object.keys(answers).length > 0 && (
              <div className="border border-[#E5E5EA] rounded-xl overflow-hidden">
                <div className="bg-[#F7F7F8] px-4 py-3 border-b border-[#E5E5EA]">
                  <h3 className="text-sm font-semibold text-[#111111]">Resumo do Diagnóstico</h3>
                </div>
                <div className="p-4 space-y-2">
                  {diagnosticQuestions.filter(q => answers[q.question_id]).map(q => (
                    <div key={q.question_id} className="flex items-start gap-2 text-sm">
                      <span className="text-[#6E6E73] min-w-[200px]">{q.text}:</span>
                      <span className="font-medium text-[#111111]">{answers[q.question_id]}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-[#FFF1F0] border border-[#FFCCC7] rounded-lg p-4">
              <p className="text-sm text-[#6E6E73]">
                Ao confirmar, o sistema criará a oportunidade e o documento com as variáveis preenchidas pelo diagnóstico.
              </p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8 pt-6 border-t border-[#E5E5EA]">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 1}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
          </Button>
          {currentStep < 5 ? (
            <Button onClick={handleNext} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="wizard-next-btn">
              Próximo <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="wizard-submit-btn">
              {loading ? 'Gerando...' : 'Confirmar e Gerar Documento'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentWizard;
